class CollectionGrid {
  constructor() {
    this.section = document.querySelector('[data-cg-section]');
    if (!this.section) return;

    this.sectionId = this.section.dataset.sectionId;
    this.filterDrawer = document.querySelector('[data-cg-filter-drawer]');
    this.sortDrawer = document.querySelector('[data-sort-drawer]');
    this.stickyBar = document.querySelector('[data-cg-sticky-bar]');
    this.pageLoader = document.querySelector('[data-cg-page-loader]');
    this.abortController = null;
    this.scrollObserver = null;

    this.initInfiniteScroll();
    this.bindSort();
    this.bindFilters();
    this.bindPriceSliders();
    this.bindFilterDrawer();
    this.bindActiveFilterLinks();
    this.bindSortDrawer();
    this.bindStickyBar();
  }

  initInfiniteScroll() {
    if (this.scrollObserver) {
      this.scrollObserver.disconnect();
      this.scrollObserver = null;
    }

    const nextUrlEl = this.section.querySelector('[data-cg-next-url]');
    if (!nextUrlEl?.textContent.trim()) return;

    const grid = this.section.querySelector('[data-cg-grid]');
    if (!grid) return;

    const lastCard = [...grid.children]
      .reverse()
      .find((el) => !el.hasAttribute('data-cg-chips'));

    if (!lastCard) return;

    this.scrollObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.loadNextPage();
        }
      },
      { rootMargin: '0px' }
    );

    this.scrollObserver.observe(lastCard);
  }

  showLoader() {
    this.pageLoader?.classList.add('is-loading');
  }

  hideLoader() {
    this.pageLoader?.classList.remove('is-loading');
  }

  async loadNextPage() {
    const nextUrlEl = this.section.querySelector('[data-cg-next-url]');
    const grid = this.section.querySelector('[data-cg-grid]');

    if (!nextUrlEl || !grid) return;

    const nextUrl = nextUrlEl.textContent.trim();
    if (!nextUrl) {
      this.scrollObserver?.disconnect();
      return;
    }

    this.scrollObserver?.disconnect();
    this.showLoader();

    try {
      const fetchUrl = new URL(nextUrl, window.location.origin);
      fetchUrl.searchParams.set('sections', this.sectionId);

      const response = await fetch(fetchUrl.toString());
      if (!response.ok) throw new Error('Fetch failed');

      const data = await response.json();
      const html = data[this.sectionId];
      if (!html) throw new Error('No section HTML');

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const newGrid = doc.querySelector('[data-cg-grid]');
      if (newGrid) {
        for (const card of [...newGrid.children]) {
          if (card.hasAttribute('data-cg-chips')) continue;
          grid.appendChild(card);
        }
      }

      const newNextUrlEl = doc.querySelector('[data-cg-next-url]');
      nextUrlEl.textContent = newNextUrlEl?.textContent.trim() ?? '';

      this.initInfiniteScroll();
    } catch {
      // Silently fail — observer re-inits via next scroll trigger if still needed
    } finally {
      this.hideLoader();
    }
  }

  bindSort() {
    const sortSelect = this.section.querySelector('[data-cg-sort]');
    if (!sortSelect) return;

    sortSelect.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortSelect.value);
      url.searchParams.delete('page');
      this.fetchAndRender(url);
    });
  }

  bindFilters() {
    document.addEventListener('change', (e) => {
      if (!e.target.closest('[data-filter-checkbox]')) return;
      this.applyFilters();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && e.target.closest('[data-filter-price]')) {
        e.preventDefault();
        this.applyFilters();
      }
    });

    document.addEventListener('focusout', (e) => {
      if (e.target.closest('[data-filter-price]')) {
        this.applyFilters();
      }
    });
  }

  bindPriceSliders() {
    const sliders = document.querySelectorAll('[data-price-slider]');
    const MIN_GAP = 100;

    for (const slider of sliders) {
      const sliderMin = slider.querySelector('[data-slider-min]');
      const sliderMax = slider.querySelector('[data-slider-max]');
      const inputMin = slider.querySelector('[data-price-input-min]');
      const inputMax = slider.querySelector('[data-price-input-max]');
      const fill = slider.querySelector('[data-slider-fill]');

      if (!sliderMin || !sliderMax || !inputMin || !inputMax || !fill) continue;

      const rangeMax = parseFloat(slider.dataset.rangeMax) || 1;

      const updateFill = () => {
        const minVal = parseFloat(sliderMin.value);
        const maxVal = parseFloat(sliderMax.value);
        const minPercent = (minVal / rangeMax) * 100;
        const maxPercent = (maxVal / rangeMax) * 100;
        fill.style.setProperty('left', `${minPercent}%`);
        fill.style.setProperty('width', `${Math.max(0, maxPercent - minPercent)}%`);
      };

      updateFill();

      sliderMin.addEventListener('input', () => {
        if (parseFloat(sliderMin.value) > parseFloat(sliderMax.value) - MIN_GAP) {
          sliderMin.value = parseFloat(sliderMax.value) - MIN_GAP;
        }
        inputMin.value = sliderMin.value;
        updateFill();
      });

      sliderMax.addEventListener('input', () => {
        if (parseFloat(sliderMax.value) < parseFloat(sliderMin.value) + MIN_GAP) {
          sliderMax.value = parseFloat(sliderMin.value) + MIN_GAP;
        }
        inputMax.value = sliderMax.value;
        updateFill();
      });

      sliderMin.addEventListener('change', () => this.applyFilters());
      sliderMax.addEventListener('change', () => this.applyFilters());

      inputMin.addEventListener('input', () => {
        const val = Math.min(
          parseFloat(inputMin.value) || 0,
          parseFloat(sliderMax.value) - MIN_GAP
        );
        sliderMin.value = val;
        updateFill();
      });

      inputMax.addEventListener('input', () => {
        const val = Math.max(
          parseFloat(inputMax.value) || 0,
          parseFloat(sliderMin.value) + MIN_GAP
        );
        sliderMax.value = val;
        updateFill();
      });
    }
  }

  applyFilters() {
    const url = new URL(window.location.href);

    const keysToDelete = [];
    url.searchParams.forEach((_, key) => {
      if (key.startsWith('filter.')) keysToDelete.push(key);
    });
    keysToDelete.forEach((key) => url.searchParams.delete(key));
    url.searchParams.delete('page');

    const sidebar = this.section.querySelector('[data-cg-sidebar]');
    const source = sidebar ?? document;

    source.querySelectorAll('[data-filter-checkbox]:checked').forEach((cb) => {
      url.searchParams.append(cb.name, cb.value);
    });

    source.querySelectorAll('[data-filter-price]').forEach((input) => {
      if (!input.value) return;
      const price = parseFloat(input.value);
      if (!isNaN(price) && price > 0) url.searchParams.set(input.name, price);
    });

    this.fetchAndRender(url);
  }

  bindActiveFilterLinks() {
    document.addEventListener('click', (e) => {
      const filterLink = e.target.closest('[data-active-filter]');
      const clearLink = e.target.closest('[data-cg-clear-filters]');

      if (filterLink) {
        e.preventDefault();
        this.fetchAndRender(new URL(filterLink.href));
      } else if (clearLink) {
        e.preventDefault();
        this.fetchAndRender(new URL(clearLink.href));
        this.closeFilterDrawer();
      }
    });
  }

  async fetchAndRender(url) {
    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    if (this.scrollObserver) {
      this.scrollObserver.disconnect();
      this.scrollObserver = null;
    }

    const fetchUrl = new URL(url);
    fetchUrl.searchParams.set('sections', this.sectionId);

    try {
      const response = await fetch(fetchUrl.toString(), {
        signal: this.abortController.signal,
      });
      if (!response.ok) throw new Error('Fetch failed');

      const data = await response.json();
      const html = data[this.sectionId];
      if (!html) throw new Error('No section HTML');

      this.renderSection(html);
      window.history.pushState({}, '', url.toString());
    } catch (error) {
      if (error.name === 'AbortError') return;
    }
  }

  renderSection(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const newSection = doc.querySelector('[data-cg-section]');
    if (!newSection) return;

    const scrollY = window.scrollY;

    const patch = (attr) => {
      const newEl = newSection.querySelector(attr);
      const currentEl = this.section.querySelector(attr);
      if (newEl && currentEl) currentEl.innerHTML = newEl.innerHTML;
    };

    patch('[data-cg-toolbar]');
    patch('[data-cg-active-filters]');
    patch('[data-cg-sidebar]');

    const newProductsArea = newSection.querySelector('[data-cg-products-area]');
    const currentProductsArea = this.section.querySelector('[data-cg-products-area]');
    if (newProductsArea && currentProductsArea) {
      currentProductsArea.innerHTML = newProductsArea.innerHTML;
      currentProductsArea.classList.remove('opacity-50', 'pointer-events-none');
    }

    const newCgDrawer = doc.querySelector('[data-cg-filter-drawer]');
    if (newCgDrawer && this.filterDrawer) {
      const newPanel = newCgDrawer.querySelector('[data-cg-filter-panel]');
      const currentPanel = this.filterDrawer.querySelector('[data-cg-filter-panel]');
      if (newPanel && currentPanel) currentPanel.innerHTML = newPanel.innerHTML;
    }

    const newSortDrawer = doc.querySelector('[data-sort-drawer]');
    if (newSortDrawer && this.sortDrawer) {
      const newOpts = newSortDrawer.querySelector('[data-sort-options]');
      const currentOpts = this.sortDrawer.querySelector('[data-sort-options]');
      if (newOpts && currentOpts) currentOpts.innerHTML = newOpts.innerHTML;
    }

    window.scrollTo(0, scrollY);
    this.bindSort();
    this.bindPriceSliders();
    this.updateIndicatorDots();
    this.initInfiniteScroll();
  }

  bindFilterDrawer() {
    if (!this.filterDrawer) return;

    document.addEventListener('click', (e) => {
      if (!e.target.closest('[data-cg-filter-toggle]')) return;
      this.openFilterDrawer();
    });

    document.addEventListener('click', (e) => {
      if (
        e.target.closest('[data-cg-filter-close]') ||
        e.target.closest('[data-cg-filter-overlay]')
      ) {
        this.closeFilterDrawer();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !this.filterDrawer.classList.contains('hidden')) {
        this.closeFilterDrawer();
      }
    });
  }

  openFilterDrawer() {
    if (!this.filterDrawer) return;

    const overlay = this.filterDrawer.querySelector('[data-cg-filter-overlay]');
    const panel = this.filterDrawer.querySelector('[data-cg-filter-panel]');

    this.filterDrawer.classList.remove('hidden');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        overlay?.classList.remove('opacity-0');
        overlay?.classList.add('opacity-100');
        panel?.classList.remove('-translate-x-full');
        panel?.classList.add('translate-x-0');
      });
    });
    document.body.classList.add('overflow-hidden');

    const drawerAccordions = this.filterDrawer.querySelectorAll('[data-accordion-trigger]');
    for (const trigger of drawerAccordions) {
      delete trigger.dataset.accordionInitialized;
    }
    if (typeof initAccordions === 'function') initAccordions();
  }

  closeFilterDrawer() {
    if (!this.filterDrawer) return;

    const overlay = this.filterDrawer.querySelector('[data-cg-filter-overlay]');
    const panel = this.filterDrawer.querySelector('[data-cg-filter-panel]');

    overlay?.classList.remove('opacity-100');
    overlay?.classList.add('opacity-0');
    panel?.classList.remove('translate-x-0');
    panel?.classList.add('-translate-x-full');

    setTimeout(() => {
      this.filterDrawer.classList.add('hidden');
      document.body.classList.remove('overflow-hidden');
    }, 300);
  }

  bindSortDrawer() {
    if (!this.sortDrawer) return;

    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-sort-toggle]')) this.openSortDrawer();
    });

    document.addEventListener('click', (e) => {
      if (
        e.target.closest('[data-sort-close]') ||
        e.target.closest('[data-sort-overlay]')
      ) {
        this.closeSortDrawer();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !this.sortDrawer.classList.contains('hidden')) {
        this.closeSortDrawer();
      }
    });

    document.addEventListener('click', (e) => {
      const optionBtn = e.target.closest('[data-sort-option]');
      if (!optionBtn) return;

      const { sortValue } = optionBtn.dataset;
      if (!sortValue) return;

      this.closeSortDrawer();
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortValue);
      url.searchParams.delete('page');
      this.fetchAndRender(url);
    });
  }

  openSortDrawer() {
    if (!this.sortDrawer) return;

    const overlay = this.sortDrawer.querySelector('[data-sort-overlay]');
    const panel = this.sortDrawer.querySelector('[data-sort-panel]');

    this.sortDrawer.classList.remove('hidden');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        overlay?.classList.remove('opacity-0');
        overlay?.classList.add('opacity-100');
        panel?.classList.remove('translate-y-full');
        panel?.classList.add('translate-y-0');
      });
    });
    document.body.classList.add('overflow-hidden');
  }

  closeSortDrawer() {
    if (!this.sortDrawer) return;

    const overlay = this.sortDrawer.querySelector('[data-sort-overlay]');
    const panel = this.sortDrawer.querySelector('[data-sort-panel]');

    overlay?.classList.remove('opacity-100');
    overlay?.classList.add('opacity-0');
    panel?.classList.remove('translate-y-0');
    panel?.classList.add('translate-y-full');

    setTimeout(() => {
      this.sortDrawer.classList.add('hidden');
      document.body.classList.remove('overflow-hidden');
    }, 300);
  }

  updateIndicatorDots() {
    const url = new URL(window.location.href);

    let hasActiveFilters = false;
    url.searchParams.forEach((_, key) => {
      if (key.startsWith('filter.')) hasActiveFilters = true;
    });

    const defaultSort = this.stickyBar?.dataset.defaultSort ?? '';
    const currentSort = url.searchParams.get('sort_by') ?? '';
    const isSortActive = currentSort !== '' && currentSort !== defaultSort;

    for (const dot of document.querySelectorAll('[data-cg-filter-indicator]')) {
      dot.classList.toggle('hidden', !hasActiveFilters);
    }

    for (const dot of document.querySelectorAll('[data-sort-indicator]')) {
      dot.classList.toggle('hidden', !isSortActive);
    }
  }

  bindStickyBar() {
    if (!this.stickyBar) return;

    const toolbar = this.section.querySelector('[data-cg-toolbar]');
    const footer = document.querySelector('.footer');
    if (!toolbar) return;

    const LG_BREAKPOINT = 1024;
    let toolbarAbove = false;
    let footerVisible = false;

    const showBar = () => {
      this.stickyBar.classList.remove('opacity-0', 'invisible', 'translate-y-4');
      this.stickyBar.classList.add('opacity-100', 'visible', 'translate-y-0');
    };

    const hideBar = () => {
      this.stickyBar.classList.add('opacity-0', 'invisible', 'translate-y-4');
      this.stickyBar.classList.remove('opacity-100', 'visible', 'translate-y-0');
    };

    const updateBar = () => {
      requestAnimationFrame(() => {
        if (toolbarAbove && !footerVisible) {
          showBar();
        } else {
          hideBar();
        }
      });
    };

    const toolbarObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          toolbarAbove = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        }
        updateBar();
      },
      { threshold: 0 }
    );

    const footerObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          footerVisible = entry.isIntersecting;
        }
        updateBar();
      },
      { threshold: 0 }
    );

    let isObserving = false;

    const attachObserver = () => {
      if (window.innerWidth < LG_BREAKPOINT && !isObserving) {
        toolbarObserver.observe(toolbar);
        if (footer) footerObserver.observe(footer);
        isObserving = true;
      } else if (window.innerWidth >= LG_BREAKPOINT && isObserving) {
        toolbarObserver.disconnect();
        footerObserver.disconnect();
        isObserving = false;
        hideBar();
      }
    };

    attachObserver();

    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(attachObserver, 150);
    });
  }
}

window.addEventListener('popstate', () => {
  window.location.reload();
});

document.addEventListener('DOMContentLoaded', () => {
  new CollectionGrid();
});
